import type {
  StripeTypes,
  UserWithKeycloakId,
  v2
} from '@deque/billing-service-client';
import type { Subscription } from '@deque/billing-utils';
import { Loader } from '@deque/cauldron-react';
import React, { type ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useAxeAccountAnalytics } from '~/common/contexts/analytics';
import { useServerInfo } from '~/modules/common/contexts/serverInfo';
import {
  dquTransactionNotification,
  type DquTransactionNotificationParams,
  type HttpError,
  updateAccount
} from '../../common/api-client';
import DowngradeDialog from '../../common/components/DowngradeDialog';
import DQUPurchaseNotification from '../../common/components/DQUPurchaseNotification';
import PageTitle from '../../common/components/PageTitle';
import { ProductSlugs } from '../../common/constants';
import { useAuthenticatedContext } from '../../common/contexts/auth';
import { useConfiguration } from '../../common/contexts/Configuration';
import { useEnterprises } from '../../common/contexts/enterprises';
import { useGlobalToast } from '../../common/contexts/globalToast';
import { useProducts } from '../../common/contexts/products';
import useDowngrade from '../../common/hooks/useDowngrade';
import usePaymentMethods from '../../common/hooks/usePaymentMethods';
import useV2ListLicenses from '../../common/hooks/useV2ListLicenses';
import billingClient from '../../common/utils/billing-client/client-v1';
import billingClientV2 from '../../common/utils/billing-client/client-v2';
import BillingComponent from '../components/Billing';
import getStatusMessage from '../utils/get-status-message';

const Billing = (): ReactElement => {
  const { t } = useTranslation();
  const analytics = useAxeAccountAnalytics();
  const { setContents } = useGlobalToast();
  const downgradeState = useDowngrade();
  const {
    activeEnterprise,
    isAdmin,
    defaultPaymentMethod: enterpriseDefaultPaymentMethod,
    loading: loadingEnterprises,
    error: loadingEnterpriseError
  } = useEnterprises();
  const { serverInfo, loading: loadingServerInfo } = useServerInfo();
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [billingUser, setBillingUser] = useState<UserWithKeycloakId>();
  const [expiryDateText, setExpiryDateText] = useState('');
  const [paymentMethod, setPaymentMethod] =
    useState<StripeTypes.PaymentMethod>();
  const [companyText, setCompanyText] = useState('');
  const [performingRequest, setPerformingRequest] = useState(false);
  const [subscriptionToCancel, setSubscriptionToCancel] =
    useState<Subscription>();
  const loaderRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<StripeTypes.Invoice[]>([]);
  const [downgradeDialogOpen, setDowngradeDialogOpen] = useState(false);
  const history = useHistory();
  const authContext = useAuthenticatedContext();
  const {
    products,
    loading: productsLoading,
    error: productsError
  } = useProducts();
  const {
    settings,
    loading: configLoading,
    loadingError: configLoadingError
  } = useConfiguration();
  const {
    loading: loadingUserPaymentMethods,
    error: loadingUserPaymentMethodsError,
    defaultPaymentMethod: userDefaultPaymentMethod
  } = usePaymentMethods();

  const user = authContext.user;
  const billingUserV2 = authContext.billingUser;

  const {
    licenses: enterpriseLicenses,
    loading: loadingLicenses,
    error: licenseError,
    refresh: refreshLicenses
  } = useV2ListLicenses(user.token, {
    enterpriseId: activeEnterprise?.id || null
  });

  useEffect(() => {
    analytics.viewBillingPage();
  }, []);

  const getData = async (force = false) => {
    /* istanbul ignore next*/
    if (billingUser && !force) {
      return;
    }
    // return if the payment methods are still loading
    if (
      loadingEnterprises ||
      loadingUserPaymentMethods ||
      loadingLicenses ||
      loadingServerInfo
    ) {
      return;
    }

    try {
      // Break if there is any error in loading payment methods
      if (loadingEnterpriseError || loadingUserPaymentMethodsError) {
        throw loadingEnterpriseError || loadingUserPaymentMethodsError;
      }

      const [bUser, userInvoices] = await Promise.all([
        billingClient.me.get(user.token),
        billingClient.invoices.list(
          user.token,
          serverInfo?.isSelfProvisioningEnabled
        )
      ]);

      setBillingUser(bUser);
      setInvoices(userInvoices);

      let pm;
      if (activeEnterprise) {
        pm = enterpriseDefaultPaymentMethod;
      } else {
        pm = userDefaultPaymentMethod;
      }

      setPaymentMethod(pm);

      setCompanyText(billingUserV2?.enterprises[0]?.name || user.company || ''); //TODO: Change this when we support multiple enterprises.

      const subscriptions = activeEnterprise
        ? activeEnterprise.subscriptions
        : billingUserV2?.subscriptions || [];

      const axeSubscription = (subscriptions as Subscription[]).find(
        s => s.product_slug === ProductSlugs.axeDevToolsExtension
      );

      if (axeSubscription) {
        const { statusMessage } = getStatusMessage({
          subscription: axeSubscription,
          t
        });

        setExpiryDateText(statusMessage || '');
      }
      /* istanbul ignore next */

      setLoading(false);
    } catch (err) {
      if ((err as HttpError).status === 401) {
        authContext.login(); // should redirect
      }
      setError(
        t('Could not load user information: {{ error }}', {
          error: (err as HttpError).message
        })
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [
    user,
    billingUserV2,
    loadingEnterprises,
    loadingUserPaymentMethods,
    loadingLicenses,
    loadingServerInfo,
    userDefaultPaymentMethod,
    enterpriseDefaultPaymentMethod,
    activeEnterprise
  ]);

  const handleRemovePaymentMethodClick = () => {
    setConfirming(true);
  };

  const handleConfirmRemoveClick = async () => {
    /* istanbul ignore next */
    if (!paymentMethod) {
      return;
    }
    try {
      /* istanbul ignore next */
      if (loaderRef?.current) {
        loaderRef.current.focus();
      }
      setPerformingRequest(true);
      const pm = await billingClient.users.removePaymentMethod(
        user.token,
        paymentMethod.id
      );
      await authContext.updateBillingUser();
      /* istanbul ignore else */
      if (pm && pm.id === paymentMethod.id) {
        setPaymentMethod(undefined);
      }

      setConfirming(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setPerformingRequest(false);
    }
  };

  const handleCancelRemoveClick = () => {
    setConfirming(false);
  };

  const onAssignLicenses = async (
    subscription: v2.EnterpriseSubscription,
    newLicenseCount: number | null
  ) => {
    // impossible but makes typescript happy
    /* istanbul ignore next */
    if (!subscription?.stripe_id) {
      return;
    }

    setLoading(true);

    try {
      if (!newLicenseCount) {
        throw new Error('Invalid license count');
      }

      await billingClient.subscriptions.update({
        token: user.token,
        enterpriseId: subscription.enterprise_id,
        subscriptionId: subscription.stripe_id,
        licenseCount: newLicenseCount
      });

      if (subscription.product_slug === ProductSlugs.dequeUniversity) {
        const body: DquTransactionNotificationParams = {
          newSubscription: false,
          priceId: subscription.pricing?.stripe_id,
          quantity: newLicenseCount
        };

        await dquTransactionNotification(user.token, body);

        setContents(<DQUPurchaseNotification />, 'info');
      } else {
        setContents(
          t(
            'Your number of subscriptions has been updated to {{ newLicenseCount }} successfully.',
            {
              newLicenseCount
            }
          ),
          'confirmation'
        );
      }

      await authContext.updateBillingUser();
      await refreshLicenses();

      getData(true);
    } catch {
      setError(t('Failed to change user subscriptions. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const onAssignEnterpriseName = async (name: string) => {
    if (!billingUserV2) {
      return null;
    }

    try {
      const enterprise = await billingClientV2.enterprises.create({
        token: user.token,
        name,
        admin_keycloak_id: billingUserV2.keycloak_id
      });
      return enterprise;
    } catch (err) {
      if ((err as HttpError).status === 409) {
        throw err;
      }
      setError(t('Failed to create enterprise. Please try again.'));
      return null;
    }
  };

  const onCompanyEditSubmit = async (name: string) => {
    setPerformingRequest(true);

    try {
      if (activeEnterprise) {
        await billingClient.enterprises.update({
          token: user.token,
          enterpriseId: activeEnterprise.id,
          name
        });
      } else {
        await updateAccount(user.id, { company: name }, user.token);
      }
      setCompanyText(name);
      setContents(
        t('Company information was updated successfully'),
        'confirmation'
      );
    } catch {
      setError(t('Failed to update company information. Please try again.'));
    } finally {
      setPerformingRequest(false);
    }
  };

  const onCancelSubscriptionClick = (sub: Subscription) => {
    setSubscriptionToCancel(sub);
    setDowngradeDialogOpen(true);
  };
  const onDowngradeDialogClose = () => setDowngradeDialogOpen(false);
  const onDowngrade = async () => {
    /* istanbul ignore if */
    if (!user || !billingUserV2) {
      return;
    }

    // close the modal
    onDowngradeDialogClose();

    // downgrade the user
    if (subscriptionToCancel) {
      await downgradeState.downgrade({
        user,
        subscription: subscriptionToCancel
      });
    }

    // we've successfully downgraded
    if (
      // route to plans now
      subscriptionToCancel?.product_slug === ProductSlugs.axeDevToolsExtension
    ) {
      history.push('/plans?utm_campaign=webapp_plans');
    } else {
      // Refresh the data
      setLoading(true);
      await authContext.updateBillingUser();
      await getData(true);
      setLoading(false);
    }
  };

  const showLoader =
    loading ||
    authContext.loading ||
    productsLoading ||
    downgradeState.loading ||
    loadingEnterprises ||
    (activeEnterprise && loadingLicenses) ||
    configLoading;
  const errorMessage =
    error ||
    productsError?.message ||
    configLoadingError?.message ||
    (activeEnterprise && licenseError?.message) ||
    downgradeState.error;

  const subscriptions =
    activeEnterprise?.subscriptions || billingUserV2?.subscriptions || [];

  return (
    <>
      <PageTitle title={t('Manage Billing')} />
      {showLoader ? (
        <Loader label={t('Loading')} ref={loaderRef} tabIndex={-1} />
      ) : (
        <>
          <BillingComponent
            removePaymentMethodClick={handleRemovePaymentMethodClick}
            confirmRemoveClick={handleConfirmRemoveClick}
            cancelRemoveClick={handleCancelRemoveClick}
            confirming={confirming}
            majorHeading={t('Manage Billing')}
            subscriptionEnd={expiryDateText}
            companyText={companyText}
            paymentMethod={paymentMethod}
            loaderRef={loaderRef}
            performingRequest={performingRequest}
            licenses={enterpriseLicenses || []}
            onAssignLicenses={onAssignLicenses}
            onAssignEnterpriseName={onAssignEnterpriseName}
            error={errorMessage}
            subscriptions={subscriptions}
            billingUser={billingUser}
            products={products}
            invoices={invoices}
            onCompanyEditSubmit={onCompanyEditSubmit}
            isBillingAdmin={isAdmin}
            onCancelSubscriptionClick={onCancelSubscriptionClick}
            hideExpiredProducts={
              settings?.general.expiredProductVisibility.value === 'none'
            }
            activeEnterprise={activeEnterprise}
            setError={setError}
          />
          {subscriptionToCancel && (
            <DowngradeDialog
              show={downgradeDialogOpen}
              onClose={onDowngradeDialogClose}
              onConfirm={onDowngrade}
              isPaidPlan={subscriptionToCancel?.purchase_state === 'paid'}
              subscription={subscriptionToCancel}
            />
          )}
        </>
      )}
    </>
  );
};

export default Billing;
